import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { COUNTRIES, SERVICE_INTERESTS } from "@/data/content";

export default function DemoDialog({ open, onOpenChange, prefill = {} }) {
  const [form, setForm] = useState({
    full_name: "", company: "", email: "", phone: "",
    country: "", message: "", service_interest: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      setSuccess(false);
      setForm((f) => ({ ...f, ...prefill }));
    }
  }, [open, prefill]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/leads", form);
      setSuccess(true);
      toast.success("Thanks! Our team will reach out within 24 hours.");
      setForm({ full_name: "", company: "", email: "", phone: "", country: "", message: "", service_interest: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="demo-dialog"
        className="max-w-2xl bg-[#0B1224] border-white/10 text-white p-0 overflow-hidden"
      >
        {success ? (
          <div className="p-10 text-center" data-testid="demo-success">
            <div className="mx-auto w-14 h-14 rounded-full bg-[#2563EB]/20 border border-[#2563EB]/40 flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-[#00D4FF]" />
            </div>
            <h3 className="mt-6 font-display text-2xl font-bold">Thank you!</h3>
            <p className="mt-2 text-gray-400 max-w-md mx-auto">
              Our iGaming solutions team will reach out to you within 24 hours. Meanwhile, feel free to explore our solutions.
            </p>
            <Button
              data-testid="demo-success-close"
              onClick={() => onOpenChange(false)}
              className="mt-6 btn-glow bg-[#2563EB] hover:bg-[#1d4ed8] rounded-full h-11 px-6"
            >
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader className="p-6 pb-2 border-b border-white/10">
              <div className="overline">Book a Demo</div>
              <DialogTitle className="font-display text-2xl font-bold tracking-tight">
                Let's talk about your iGaming project
              </DialogTitle>
              <DialogDescription className="text-gray-400">
                Fill the form and our solutions team will reach out within 24 hours.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs text-gray-400">Full Name *</Label>
                <Input required data-testid="demo-full-name" value={form.full_name} onChange={set("full_name")}
                  className="bg-black/30 border-white/10 focus-visible:ring-[#2563EB]" placeholder="Jane Cooper"/>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-gray-400">Company *</Label>
                <Input required data-testid="demo-company" value={form.company} onChange={set("company")}
                  className="bg-black/30 border-white/10 focus-visible:ring-[#2563EB]" placeholder="Acme Gaming"/>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-gray-400">Email *</Label>
                <Input required type="email" data-testid="demo-email" value={form.email} onChange={set("email")}
                  className="bg-black/30 border-white/10 focus-visible:ring-[#2563EB]" placeholder="you@company.com"/>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-gray-400">Phone *</Label>
                <Input required data-testid="demo-phone" value={form.phone} onChange={set("phone")}
                  className="bg-black/30 border-white/10 focus-visible:ring-[#2563EB]" placeholder="+44 7000 000000"/>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-gray-400">Country *</Label>
                <Select value={form.country} onValueChange={(v)=>setForm(f=>({...f, country:v}))}>
                  <SelectTrigger data-testid="demo-country" className="bg-black/30 border-white/10 text-white">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0B1224] border-white/10 text-white max-h-64">
                    {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-gray-400">Interested In</Label>
                <Select value={form.service_interest} onValueChange={(v)=>setForm(f=>({...f, service_interest:v}))}>
                  <SelectTrigger data-testid="demo-service" className="bg-black/30 border-white/10 text-white">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0B1224] border-white/10 text-white">
                    {SERVICE_INTERESTS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2 space-y-2">
                <Label className="text-xs text-gray-400">Message</Label>
                <Textarea data-testid="demo-message" value={form.message} onChange={set("message")}
                  rows={4} className="bg-black/30 border-white/10 focus-visible:ring-[#2563EB]"
                  placeholder="Tell us about your project, target markets, timeline..."/>
              </div>

              <div className="md:col-span-2 flex items-center justify-between pt-2">
                <p className="text-xs text-gray-500">By submitting, you agree to our terms & privacy policy.</p>
                <Button
                  type="submit"
                  data-testid="demo-submit"
                  disabled={submitting || !form.country}
                  className="btn-glow bg-[#2563EB] hover:bg-[#1d4ed8] rounded-full h-11 px-7 text-white"
                >
                  {submitting ? "Sending..." : "Book Demo"}
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
